import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import { Lead, LeadScreenStore, ViewType } from '../../../stores/LeadStore';
import PerfectScrollbar from 'react-perfect-scrollbar';
import Grid from '@mui/material/Grid';
import DoActivityLeadInformation from './DoActivityLeadInformation';
import { LeadDataFormater } from 'src/features/leads/view/LeadDataFormater';
import { LeadAccountComponent } from 'src/features/leads/view/LeadAccountComponent';
import LeadForm from './LeadForm';

import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import Drawer from '@mui/material/Drawer';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Box, { BoxProps } from '@mui/material/Box';
import { useForm } from 'react-hook-form';
import Close from 'mdi-material-ui/Close';
import { v4 as uuidv4 } from 'uuid';
import { client } from 'src/utils/axios-client';
import { ProblemDetails } from 'src/types/ApiErrorResponse';
import { Button, IconButton, Tooltip } from '@mui/material';
import { useEffect } from 'react';
import { getLeadEndpoint, leadEndpoints } from 'src/configs/endpoints';
import { LeadListStore } from 'src/stores/LeadStore';
import _ from 'lodash';
import Paper, { PaperProps } from '@mui/material/Paper';
import paper from '../../../materio/@core/theme/overrides/paper';
import LeadReferralForm from './LeadReferralForm';

type Props = {
    open: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    title: string;
};

const schema = yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    emailAddress: yup.string().required(),
    jobTitle: yup.string().required(),
    industry: yup.object().shape({ id: yup.string().required() }),
    country: yup.object().shape({ id: yup.string().required() }),
});

const defaultValues = {
    id: uuidv4(),
    firstName: '',
    lastName: '',
    emailAddress: '',
    industry: {
        id: '',
        name: '',
    },
    country: {
        id: '',
        name: '',
    },
    fullName: '',
    jobTitle: '',
    company: '',
    directPhone: '',
    mobilePhone: '',
    switchboardPhone: '',
    phoneExtension: '',
    addressStreet: '',
    addressCity: '',
    addressProvince: '',
    addressPostalCode: '',
    website: '',
    linkedIn: '',
    twitter: '',
    salutation: '',
    leadLevel: {
        id: '',
        name: '',
    },
    leadStage: {
        id: '',
        name: '',
        stageColor: '',
    },
    account: {
        id: '',
        name: '',
        revenue: {
            id: '',
            name: '',
        },
        employeeSize: {
            id: '',
            name: '',
        },
    },
    hotLead: false,
    meaningfulInteraction: false,
    lastActivityDateTime: '',
    lastActivity: {
        id: '',
        name: '',
        icon: 'none',
    },
    lastActivityOwner: {
        id: '',
        fullName: '',
        avatarFileName: '',
    },
    nextActivityDateTime: '',
    nextActivity: {
        id: '',
        name: '',
        icon: 'none',
    },
    nextActivityOwner: {
        id: '',
        fullName: '',
        avatarFileName: '',
    },
    leadFunction: {
        id: '',
        name: '',
    },
    leadSource: {
        id: '',
        name: '',
    },
    addressRegion: {
        id: '',
        name: '',
    },
    fax: '',
    sprint: {
        id: '',
        name: '',
    },
    owner: {
        id: '',
        fullName: '',
        avatarFileName: '',
    },
    nextActivityCadenceStep: {
        id: '',
        processOrder: 0,
        playbookCadence: {
            id: '',
            name: '',
        },
    },
    switchboardEmail: '',
    customer: {
        id: '',
        name: '',
    },
    dncPhone: false,
    dncMobile: false,
    dncSwitchboard: false,
    referralFrom: {
        id: '',
        fullName: '',
    },
    referralTo: {
        id: '',
        fullName: '',
    },
};

export default function EditDialog({ open, onConfirm, onCancel, title }: Props) {
    const editMode = LeadScreenStore.useState((s) => s.editMode);
    const lead = LeadScreenStore.useState((s) => s.selectedLead) as Lead;
    const isNewPopup = LeadScreenStore.useState((s) => s.isNewPopup);
    const isAddReferralPopup = LeadScreenStore.useState((s) => s.isAddReferralPopup);
    const referralLead = LeadScreenStore.useState((s) => s.referralLead) as Lead;

    const {
        reset,
        control,
        setError,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: defaultValues,
        mode: 'onChange',
        resolver: yupResolver(schema),
    });

    // Reset form values when lead changes
    useEffect(() => {
        if (!open) {
            reset(defaultValues);
            return;
        }
        if (lead) {
            reset(lead);
            if (isAddReferralPopup) {
                LeadScreenStore.update((s, o) => {
                    s.referralLead = {
                        ...o.selectedLead!,
                        firstName: '',
                        lastName: '',
                        emailAddress: '',
                        salutation: '',
                        jobTitle: '',
                        linkedIn: '',
                        mobilePhone: '',
                        directPhone: '',
                        dncMobile: false,
                        dncPhone: false,
                        dncSwitchboard: false,
                    };
                });
            }
        }
        if (!lead) reset(defaultValues);
    }, [editMode, lead]);

    useEffect(() => {
        reset(referralLead);
    }, [referralLead]);

    const onSubmitEditLead = async (data: Lead) => {
        await client
            .post(leadEndpoints.updateEndpoint, data)
            .then(async (response) => {
                LeadScreenStore.update((s) => {
                    s.editMode = false;
                });

                const updatedLeadResposne = await client.get(getLeadEndpoint.replace('{id}', data.id));

                LeadListStore.update((s) => {
                    const list = _.cloneDeep(s.list);
                    const leadIndex = list.items.findIndex((s) => s.id == data.id);
                    const existingLead = list.items[leadIndex];
                    list.items[leadIndex] = { ...existingLead, ...updatedLeadResposne.data };
                    s.list = list;
                });

                LeadScreenStore.update((s, o) => {
                    const lead = _.cloneDeep(o.selectedLead);
                    s.selectedLead = { ...lead, ...updatedLeadResposne.data };

                    if (o.selectedLeadCard) {
                        s.selectedLeadCard = { ...lead, ...updatedLeadResposne.data };
                    }
                });

                onCancel();

                reset();
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const onSubmitAddLead = async (data: Lead) => {
        await client
            .post(leadEndpoints.createreferrallead, data)
            .then(async (response) => {
                LeadScreenStore.update((s) => {
                    s.editMode = false;
                });

                const updatedLeadResposne = await client.get(getLeadEndpoint.replace('{id}', data.id));

                LeadListStore.update((s) => {
                    const list = _.cloneDeep(s.list);
                    const leadIndex = list.items.findIndex((s) => s.id == data.id);
                    const existingLead = list.items[leadIndex];
                    list.items[leadIndex] = { ...existingLead, ...updatedLeadResposne.data };
                    s.list = list;
                });

                LeadScreenStore.update((s, o) => {
                    const lead = _.cloneDeep(s.selectedLead);
                    s.selectedLead = { ...lead, ...updatedLeadResposne.data };

                    if (o.selectedLeadCard) {
                        s.selectedLeadCard = { ...lead, ...updatedLeadResposne.data };
                    }
                });

                onCancel();

                reset();
            })
            .catch((err: ProblemDetails) => {
                if (err.errors) {
                    for (const [key, value] of Object.entries(err.errors)) {
                        const errorMessage = '';

                        value.forEach((message) => {
                            message += message + '\\';
                        });
                    }
                }
            });
    };

    const handleLeadClose = () => {
        LeadScreenStore.update((s) => {
            s.editMode = false;
        });

        defaultValues.id = uuidv4();
        reset(defaultValues);
    };
    return (
        <Dialog
            fullWidth={true}
            maxWidth="md"
            open={open}
            onClose={onCancel}
            scroll="body"
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
        >
            <DialogContent>
                {isNewPopup && (
                    <LeadForm
                        onSubmit={handleSubmit(onSubmitEditLead)}
                        handleClose={onCancel}
                        control={control}
                        errors={errors}
                        lead={lead}
                    />
                )}
                {isAddReferralPopup && (
                    <LeadReferralForm
                        onSubmit={handleSubmit(onSubmitAddLead)}
                        handleClose={onCancel}
                        control={control}
                        errors={errors}
                        lead={referralLead}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
